<script setup lang="ts">
import { SwitchComponent, UploadInput } from '@metapic-apps/shared'
import type { UploadRawFile } from 'element-plus'
import { watch } from 'vue'

import HelpIcon from '@/assets/icons/help.svg?component'
import TodoList from '@/components/CampaignForm/TodoList.vue'
import { type CampaignFormType, CampaignTypeEnum } from '@/lib/types/campaign.types'
import type { ProcessableImageType } from '@/lib/types/common.types'
import {
  BoxComponent,
  FlexComponent,
  InputComponent,
  RadioButton,
  RadioButtonGroupComponent,
  TitleComponent,
  TypographyComponent,
} from '@/ui-library'

defineProps<{ image?: ProcessableImageType }>()

const campaignType = defineModel<CampaignFormType['campaignType']>('campaignType')
const internalName = defineModel<CampaignFormType['internalName']>('internalName')
const title = defineModel<CampaignFormType['title']>('title')
const description = defineModel<CampaignFormType['description']>('description')
const hasProductSeeding = defineModel<CampaignFormType['hasProductSeeding']>('hasProductSeeding')
const isShownForAdvertiser = defineModel<CampaignFormType['isShownForAdvertiser']>('isShownForAdvertiser')
const hasTodoItems = defineModel<CampaignFormType['hasTodoItems']>('hasTodoItems')
const hasBadge = defineModel<CampaignFormType['hasBadge']>('hasBadge')
const todoItems = defineModel<CampaignFormType['todoList']>('todoItems')
const tempCampaignImage = defineModel<UploadRawFile | Blob | undefined>('tempCampaignImage')

watch(
  () => campaignType.value,
  () => {
    if (campaignType.value === CampaignTypeEnum.Standard) {
      isShownForAdvertiser.value = false
    }
  },
)

const campaignTypesMap = [
  {
    value: CampaignTypeEnum.Standard,
    label: 'Without approval (Offer)',
    tooltip: {
      title: 'Without approval',
      content:
        'This campaign type simply overwrites the default commission. Once created, the updated commissions are active for creators without needing their acceptance.',
    },
  },
  {
    value: CampaignTypeEnum.UserAccepts,
    label: 'Creator accepts',
    tooltip: {
      title: 'Creator accepts',
      content:
        'This campaign type includes additional features such as product seeding, a to-do list, and campaign management in the advertiser app. Once created, creators must accept the campaign in their Metapic creator app.',
    },
  },
  {
    value: CampaignTypeEnum.StoreAccepts,
    label: 'Advertiser approves creators',
    tooltip: {
      title: 'Advertiser approves creators',
      content:
        'This campaign type includes additional features such as product seeding, a to-do list, and campaign management in the advertiser app. The advertiser must approve creators for them to participate.',
    },
  },
  {
    value: CampaignTypeEnum.Suggestion,
    label: 'Suggestions',
    tooltip: {
      title: 'Suggestions',
      content:
        'This campaign type includes additional features such as product seeding, a to-do list, and campaign management in the advertiser app. The advertiser must approve creators from a list of suggested ones for them to participate.',
    },
  },
]
</script>

<template>
  <RadioButtonGroupComponent v-model="campaignType" name="campaignType">
    <RadioButton v-for="item in campaignTypesMap" :key="item.value" :value="item.value" :label="item.label">
      <template #content>
        <FlexComponent :gap="8" justify="center">
          <TypographyComponent variant="label-large">{{ item.label }}</TypographyComponent>
          <el-tooltip placement="top" effect="light" :show-arrow="false">
            <el-icon :size="18" color="var(--secondary-18)">
              <HelpIcon />
            </el-icon>
            <template #content>
              <BoxComponent :w="280">
                <BoxComponent :mb="4">
                  <TypographyComponent variant="title-medium">{{ item.tooltip.title }}</TypographyComponent>
                </BoxComponent>
                <TypographyComponent variant="body-medium">
                  {{ item.tooltip.content }}
                </TypographyComponent>
              </BoxComponent>
            </template>
          </el-tooltip>
        </FlexComponent>
      </template>
    </RadioButton>
  </RadioButtonGroupComponent>

  <el-form-item prop="internalName">
    <InputComponent v-model="internalName" name="internalName">
      <template #header>
        <BoxComponent :mb="8">
          <TitleComponent title="Internal name">
            <template #tooltip>
              <BoxComponent :w="280">
                <BoxComponent :mb="4">
                  <TypographyComponent variant="title-medium">Internal name</TypographyComponent>
                </BoxComponent>
                <TypographyComponent variant="body-medium">
                  This name will be displayed in lists and cards for organizing your campaigns. It will not be visible to the public.
                </TypographyComponent>
              </BoxComponent>
            </template>
          </TitleComponent>
        </BoxComponent>
      </template>
    </InputComponent>
  </el-form-item>

  <template v-if="campaignType === CampaignTypeEnum.Standard">
    <SwitchComponent v-model="hasBadge" title="Add badge to advertiser card" name="hasBadge">
      <template #tooltip>
        <BoxComponent :w="280">
          <BoxComponent :mb="4">
            <TypographyComponent variant="title-medium">Badge</TypographyComponent>
          </BoxComponent>
          <TypographyComponent variant="body-medium">
            A badge appears in the top right corner of an advertiser card in the Creator App. Hovering over it reveals a description.
            <br />
            Example:
            <BoxComponent :mt="4">
              <img src="../../assets/images/example-badge.jpg" alt="Badge example" class="example-image" />
            </BoxComponent>
          </TypographyComponent>
        </BoxComponent>
      </template>
    </SwitchComponent>

    <template v-if="hasBadge">
      <InputComponent v-model="title" name="title">
        <template #header>
          <BoxComponent :mb="8">
            <TitleComponent title="Title" />
          </BoxComponent>
        </template>
      </InputComponent>

      <InputComponent v-model="description" type="textarea" :rows="4" name="description">
        <template #header>
          <BoxComponent :mb="8">
            <TitleComponent title="Description" />
          </BoxComponent>
        </template>
      </InputComponent>
    </template>
  </template>

  <template v-else>
    <el-form-item prop="title">
      <InputComponent v-model="title" name="title">
        <template #header>
          <BoxComponent :mb="8">
            <TitleComponent title="Title">
              <template #tooltip>
                <BoxComponent :w="280">
                  <BoxComponent :mb="4">
                    <TypographyComponent variant="title-medium">Title</TypographyComponent>
                  </BoxComponent>
                  <TypographyComponent variant="body-medium">
                    This is the public campaign title, visible to creators in their campaign listing within the Creator App.
                  </TypographyComponent>
                </BoxComponent>
              </template>
            </TitleComponent>
          </BoxComponent>
        </template>
      </InputComponent>
    </el-form-item>

    <el-form-item prop="description">
      <InputComponent v-model="description" type="textarea" :rows="4" name="description">
        <template #header>
          <BoxComponent :mb="8">
            <TitleComponent title="Description" />
          </BoxComponent>
        </template>
      </InputComponent>
    </el-form-item>

    <UploadInput v-model:image-thumbnail="tempCampaignImage" prop="tempCampaignImage" placeholder="" :auto-upload="false" :image="image">
      <template #label>
        <BoxComponent :mb="8">
          <TitleComponent title="Image" />
        </BoxComponent>
      </template>
      <template #placeholder>
        <BoxComponent :mb="8">
          <FlexComponent direction="column" align="center">
            <TypographyComponent variant="body-small" color="var(--secondary-64)">
              Must be JPG or PNG, maximum file size 10MB.
            </TypographyComponent>
            <TypographyComponent variant="body-small" color="var(--secondary-64)">Best 800x450 pixels. </TypographyComponent>
          </FlexComponent>
        </BoxComponent>
      </template>
    </UploadInput>

    <SwitchComponent v-model="hasProductSeeding" title="Product seeding" name="hasProductSeeding" />

    <SwitchComponent v-model="hasTodoItems" title="To-do list" name="hasTodoItems" />

    <BoxComponent v-if="hasTodoItems">
      <TodoList v-model:todo-items="todoItems" />
    </BoxComponent>

    <SwitchComponent v-model="isShownForAdvertiser" title="Show in advertiser app" name="isShownForAdvertiser">
      <template #tooltip>
        <BoxComponent :w="280">
          <BoxComponent :mb="4">
            <TypographyComponent variant="title-medium">Show in advertiser app</TypographyComponent>
          </BoxComponent>
          <TypographyComponent variant="body-medium">
            Switching on makes this campaign visible to the advertiser in the advertiser app.
          </TypographyComponent>
        </BoxComponent>
      </template>
    </SwitchComponent>
  </template>
</template>

<style scoped lang="scss">
.example-image {
  width: 100%;
}
</style>
